<template>
  <themed-card>
    <template #header>
      <h2 class="text-xl font-semibold">Payment Methods</h2>
    </template>

    <!-- Add Payment Method -->
    <div class="payment-add-section">
      <h3 class="text-lg font-medium mb-4">Add Payment Method</h3>

      <div ref="cardEl" class="payment-card-element"></div>
      <p ref="cardErrorEl" class="payment-card-error"></p>

      <themed-button
        @click="addPaymentMethod"
        :disabled="isAdding || !stripe || !cardElement"
        class="mt-4"
      >
        <span v-if="isAdding">Adding...</span>
        <span v-else>Add Payment Method</span>
      </themed-button>
    </div>

    <!-- Payment Methods List -->
    <div v-if="listLoading" class="payment-loading">
      <p class="text-muted-foreground">Loading your payment methods…</p>
    </div>

    <div v-else-if="paymentMethods.length > 0" class="payment-list">
      <h3 class="text-lg font-medium mb-4">Your Payment Methods</h3>
      <div class="payment-methods">
        <div
          v-for="method in paymentMethods"
          :key="method.id"
          :class="['payment-method-card', { 'is-default': method.is_default }]"
        >
          <div class="payment-method-info">
            <i :class="getCardIcon(method.brand)" class="payment-card-icon"></i>
            <div>
              <p class="payment-card-number">•••• •••• •••• {{ method.last4 }}</p>
              <p class="payment-card-details">
                {{ titleCase(method.brand) }} • Expires {{ pad2(method.exp_month) }}/{{ method.exp_year }}
                <themed-badge v-if="method.is_default" variant="default" class="ml-2">
                  Default
                </themed-badge>
              </p>
            </div>
          </div>
          <div class="payment-method-actions">
            <themed-button
              v-if="!method.is_default"
              variant="ghost"
              size="sm"
              @click="setDefaultPaymentMethod(method.id)"
              :disabled="isSettingDefault === method.id"
            >
              <span v-if="isSettingDefault === method.id">Setting…</span>
              <span v-else>Set as Default</span>
            </themed-button>
            <themed-button
              variant="ghost"
              size="sm"
              @click="confirmDelete(method.id)"
              :disabled="isDeleting === method.id"
              class="text-destructive"
            >
              <span v-if="isDeleting === method.id">Deleting…</span>
              <span v-else>Remove</span>
            </themed-button>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="payment-empty">
      <p class="text-muted-foreground">No payment methods found.</p>
    </div>
  </themed-card>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import ThemedCard from '../ThemedCard.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedBadge from '../ThemedBadge.vue';

export default {
  name: 'PaymentMethods',
  components: {
    ThemedCard,
    ThemedButton,
    ThemedBadge
  },
  data() {
    return {
      stripeKey: window.STRIPE_PUBLISHABLE_KEY,
      stripe: null,
      elements: null,
      cardElement: null,
      listLoading: true,
      isAdding: false,
      isSettingDefault: null,
      isDeleting: null,
      paymentMethods: [],
    };
  },
  async mounted() {
    try {
      await this.initStripe();
    } catch (e) {
      console.error('Stripe init failed:', e);
      this.$toast?.error?.(e.message || 'Failed to initialize payment form.');
    }
    await this.refreshPaymentMethods();
  },
  beforeUnmount() {
    try {
      this.cardElement?.unmount?.();
      this.cardElement?.destroy?.();
    } catch {
      // Teardown must not throw: the element may already be unmounted.
    }
    this.cardElement = null;
    this.elements = null;
  },
  methods: {
    unwrap(res) {
      return res?.data?.data ?? res?.data ?? res ?? null;
    },
    titleCase(s) {
      if (!s) return '';
      return String(s).charAt(0).toUpperCase() + String(s).slice(1).toLowerCase();
    },
    pad2(n) {
      const v = Number(n || 0);
      return v < 10 ? `0${v}` : String(v);
    },
    getCardIcon(brand) {
      const icons = {
        visa: 'fab fa-cc-visa',
        mastercard: 'fab fa-cc-mastercard',
        amex: 'fab fa-cc-amex',
        discover: 'fab fa-cc-discover',
        jcb: 'fab fa-cc-jcb',
        diners: 'fab fa-cc-diners-club',
        unionpay: 'fab fa-cc-discover',
      };
      return icons[String(brand || '').toLowerCase()] || 'far fa-credit-card';
    },
    async initStripe() {
      if (!this.stripeKey) {
        throw new Error('Stripe publishable key missing.');
      }

      this.stripe = await loadStripe(this.stripeKey);
      if (!this.stripe) {
        throw new Error('Failed to initialize Stripe.');
      }

      this.elements = this.stripe.elements();
      this.cardElement = this.elements.create('card');

      await this.$nextTick();
      if (!this.$refs.cardEl) {
        throw new Error('Card element mount target not found.');
      }

      this.cardElement.mount(this.$refs.cardEl);
      this.cardElement.on('change', (event) => {
        if (this.$refs.cardErrorEl) {
          this.$refs.cardErrorEl.textContent = event.error ? event.error.message : '';
        }
      });
    },
    async refreshPaymentMethods() {
      this.listLoading = true;
      try {
        const res = await this.$store.dispatch('billing/fetchPaymentMethods');
        const list = this.unwrap(res) || [];
        this.paymentMethods = Array.isArray(list) ? list : [];
      } catch (e) {
        console.error(e);
        this.$toast?.error?.('Failed to load payment methods.');
        this.paymentMethods = [];
      } finally {
        this.listLoading = false;
      }
    },
    async addPaymentMethod() {
      if (!this.stripe || !this.cardElement) return;
      this.isAdding = true;
      try {
        const siRes = await this.$store.dispatch('billing/createSetupIntent');
        const setupIntent = this.unwrap(siRes);
        const clientSecret = setupIntent?.client_secret || setupIntent?.setup_intent?.client_secret;
        if (!clientSecret) {
          throw new Error('Missing client secret for Setup Intent.');
        }

        const { error } = await this.stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: this.cardElement,
          },
        });

        if (error) throw error;

        await this.refreshPaymentMethods();
        this.cardElement.clear?.();
        this.$toast?.success?.('Payment method added successfully.');
      } catch (e) {
        console.error(e);
        this.$toast?.error?.(e?.message || 'Failed to add payment method.');
      } finally {
        this.isAdding = false;
      }
    },
    async setDefaultPaymentMethod(paymentMethodId) {
      this.isSettingDefault = paymentMethodId;
      try {
        await this.$store.dispatch('billing/setDefaultPaymentMethod', paymentMethodId);
        await this.refreshPaymentMethods();
        this.$toast?.success?.('Default payment method updated.');
      } catch (e) {
        console.error(e);
        this.$toast?.error?.('Failed to update default payment method.');
      } finally {
        this.isSettingDefault = null;
      }
    },
    async confirmDelete(paymentMethodId) {
      try {
        const result = await this.$swal({
          title: 'Remove Payment Method',
          text: 'Are you sure you want to remove this payment method?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, remove it',
          cancelButtonText: 'Cancel',
          reverseButtons: true,
          focusConfirm: false,
          focusCancel: true
        });

        if (!result.value) {
          return;
        }

        await this.deletePaymentMethod(paymentMethodId);
        this.$toast?.success?.('Payment method removed successfully.');
      } catch (error) {
        console.error('Error in confirmDelete:', error);
        this.$toast?.error?.('An error occurred while processing your request.');
      }
    },
    async deletePaymentMethod(paymentMethodId) {
      this.isDeleting = paymentMethodId;
      try {
        await this.$store.dispatch('billing/deletePaymentMethod', paymentMethodId);
        await this.refreshPaymentMethods();
      } catch (e) {
        console.error(e);
        throw e;
      } finally {
        this.isDeleting = null;
      }
    },
  },
};
</script>
